import {
  CalculatorUserLayerV4ValidationError,
  parseCalculatorUserOverlayDocumentV4,
} from "./calculator-user-layer-v4-contract.js";

const MODEL_REVISION_PATTERN = /^blake3:[0-9a-f]{64}$/;
const BOUNDARIES = new Set([
  "initialization",
  "modelRevision",
  "storage",
]);

function validationError(message, response = null) {
  const error = new CalculatorUserLayerV4BoundaryError(message);
  error.response = response;
  return error;
}

function requireBoundary(boundary) {
  if (!BOUNDARIES.has(boundary)) {
    throw new CalculatorUserLayerV4BoundaryError(
      `Unsupported calculator validation boundary: ${String(boundary)}`,
    );
  }
  return boundary;
}

function requireModelRevision(modelRevision) {
  if (typeof modelRevision !== "string" || !MODEL_REVISION_PATTERN.test(modelRevision)) {
    throw new CalculatorUserLayerV4BoundaryError(
      "A canonical calculator model revision is required before validation",
    );
  }
  return modelRevision;
}

function requireValidatedResponse(response, modelRevision) {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    throw validationError("Calculator validation returned no typed response", response);
  }
  if (response.outcome !== "validated") {
    const outcome = typeof response.outcome === "string" ? response.outcome : "invalid";
    throw validationError(`Calculator validation did not succeed (${outcome})`, response);
  }
  if (response.modelRevision !== modelRevision) {
    throw validationError(
      "Calculator validation confirmed a different model revision",
      response,
    );
  }
  return response;
}

export class CalculatorUserLayerV4BoundaryError extends Error {
  constructor(message) {
    super(message);
    this.name = "CalculatorUserLayerV4BoundaryError";
    this.response = null;
  }
}

/**
 * Coordinates explicit authority checks without owning transport or scheduling.
 * The supplied validator is the sole server boundary; this class never fetches.
 */
export class CalculatorUserLayerV4ValidationBoundary {
  constructor({ validator = null, modelRevision = null } = {}) {
    this.validator = validator;
    this.modelRevision = modelRevision;
  }

  setValidator(validator) {
    if (validator !== null && typeof validator !== "function") {
      throw new TypeError("Calculator user-layer validator must be a function or null");
    }
    this.validator = validator;
  }

  setModelRevision(modelRevision) {
    this.modelRevision = requireModelRevision(modelRevision);
  }

  async validate(document, boundary) {
    const normalizedBoundary = requireBoundary(boundary);
    const normalized = parseCalculatorUserOverlayDocumentV4(document);
    const modelRevision = requireModelRevision(this.modelRevision);
    if (typeof this.validator !== "function") {
      throw new CalculatorUserLayerV4BoundaryError(
        "Calculator server validation is unavailable",
      );
    }
    const response = await this.validator(Object.freeze({
      boundary: normalizedBoundary,
      modelRevision,
      document: normalized,
      userLayer: normalized.userLayer,
    }));
    return Object.freeze({
      document: normalized,
      response: requireValidatedResponse(response, modelRevision),
    });
  }
}

export function createCalculatorUserLayerV4ValidationBoundary(options) {
  return new CalculatorUserLayerV4ValidationBoundary(options);
}

export { CalculatorUserLayerV4ValidationError };
